import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { regionSchema, RegionFormData } from "../schema/region-schema";
import { Region } from "../types/region";
import { useCreateRegion } from "../hooks/use-create-region";
import { useUpdateRegion } from "../hooks/use-update-region";
import { useUpdateRegionStatus } from "../hooks/use-update-region-status";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { usePermissionContext } from "@/components/common/permission-context";

interface RegionFormProps {
  editMode: boolean;
  initialData?: Region;
  onClose: () => void;
  // When creating, called with the new region (e.g. wizard hand-off). Falls
  // back to onClose when not provided.
  onCreated?: (region: Region) => void;
}

export default function RegionForm({
  editMode,
  initialData,
  onClose,
  onCreated,
}: RegionFormProps) {
  const form = useForm<RegionFormData>({
    resolver: zodResolver(regionSchema),
    defaultValues: initialData
      ? {
          code: initialData.code,
          display_name: initialData.display_name,
          currency: initialData.currency,
          timezone: initialData.timezone,
          default_locale: initialData.default_locale ?? "en",
          price_floor_minor: initialData.price_floor_minor,
          price_ceiling_minor: initialData.price_ceiling_minor,
          is_active: initialData.is_active,
          is_fallback: initialData.is_fallback,
          rzp_checkout_config_id: initialData.rzp_checkout_config_id ?? "",
          rzp_test_checkout_config_id:
            initialData.rzp_test_checkout_config_id ?? "",
        }
      : { is_active: true, is_fallback: false, default_locale: "en" },
  });

  // The Razorpay checkout config ids are super-admin only (enforced server-side
  // too); everyone else sees them read-only and never sends them.
  const { isSuper } = usePermissionContext();

  const createMutation = useCreateRegion();
  const updateMutation = useUpdateRegion();
  const statusMutation = useUpdateRegionStatus();

  const onSubmit = async (formData: RegionFormData) => {
    const data: RegionFormData = { ...formData };
    if (!isSuper) {
      delete data.rzp_checkout_config_id;
      delete data.rzp_test_checkout_config_id;
    }
    try {
      if (editMode && initialData?.id) {
        await updateMutation.mutateAsync({ id: initialData.id, data });
        onClose();
      } else {
        const created = await createMutation.mutateAsync(data);
        if (onCreated) onCreated(created as Region);
        else onClose();
      }
    } catch {
      toast({ title: "Error submitting the form", variant: "destructive" });
    }
  };

  const handleStatusToggle = async () => {
    if (editMode && initialData?.id) {
      await statusMutation.mutateAsync({
        id: initialData.id,
        is_active: !initialData.is_active,
      });
      onClose();
    }
  };

  const numberField = (
    field: { value: number | null | undefined; onChange: (v: number | null) => void },
    placeholder: string,
  ) => (
    <Input
      type="number"
      placeholder={placeholder}
      value={field.value ?? ""}
      onChange={(e) =>
        field.onChange(e.target.value === "" ? null : Number(e.target.value))
      }
    />
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 p-6">
        <h2 className="text-lg font-semibold">
          {editMode ? "Edit Region" : "Add Region"}
        </h2>

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Code (2-letter)</FormLabel>
              <FormControl>
                <Input placeholder="in" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="display_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Display name</FormLabel>
              <FormControl>
                <Input placeholder="India" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="currency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Currency (ISO-4217)</FormLabel>
              <FormControl>
                <Input placeholder="INR" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="timezone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Timezone (IANA)</FormLabel>
              <FormControl>
                <Input placeholder="Asia/Kolkata" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="default_locale"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Default locale</FormLabel>
              <FormControl>
                <Input placeholder="en" {...field} value={field.value ?? ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="price_floor_minor"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Price floor (minor units)</FormLabel>
              <FormControl>{numberField(field, "e.g. 10000")}</FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="price_ceiling_minor"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Price ceiling (minor units)</FormLabel>
              <FormControl>{numberField(field, "e.g. 5000000")}</FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex items-center gap-x-2">
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="pb-1.5">Active</FormLabel>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="is_fallback"
          render={({ field }) => (
            <FormItem className="flex items-start gap-x-2">
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="flex flex-col">
                <FormLabel className="pb-1">Rest of World fallback</FormLabel>
                <span className="text-xs text-muted-foreground">
                  Countries not matching any region resolve here. Turning this on
                  moves the fallback off the current Rest-of-World region.
                </span>
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="rzp_checkout_config_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Razorpay checkout config ID (live)</FormLabel>
              <FormControl>
                <Input
                  placeholder="config_…"
                  {...field}
                  value={field.value ?? ""}
                  disabled={!isSuper}
                />
              </FormControl>
              <span className="text-xs text-muted-foreground">
                {isSuper
                  ? "From the live Razorpay dashboard. Leave blank for Razorpay's default checkout."
                  : "Only a super admin can change this."}
              </span>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="rzp_test_checkout_config_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Razorpay checkout config ID (test)</FormLabel>
              <FormControl>
                <Input
                  placeholder="config_…"
                  {...field}
                  value={field.value ?? ""}
                  disabled={!isSuper}
                />
              </FormControl>
              <span className="text-xs text-muted-foreground">
                {isSuper
                  ? "From the test-mode Razorpay dashboard, used by local and staging checkouts."
                  : "Only a super admin can change this."}
              </span>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-3">
          <Button
            type="submit"
            className="bg-sidebar"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {editMode ? "Save" : "Create"}
          </Button>
          {editMode && (
            <Button
              type="button"
              variant={initialData?.is_active ? "destructive" : "default"}
              onClick={handleStatusToggle}
              disabled={statusMutation.isPending}
            >
              {initialData?.is_active ? "Deactivate" : "Activate"}
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
